import React, { useState } from "react";
import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    Heading,
    HStack,
    SimpleGrid,
    Spinner,
    Text,
    useToast,
    VStack,
} from "@chakra-ui/react";
import { useQuery, useMutation } from "@apollo/client";
import { brandConfig } from "../../brandConfig";
import { GET_CURRENT_TENANT, UPDATE_TENANT_TIER, GET_SUBSCRIPTION_PRODUCTS } from "./queries";
import { SubscriptionPlanCard, SubscriptionPlan } from "./components/SubscriptionPlanCard";
import { AddonsSection } from "./components/AddonsSection";

const SubscriptionOffers = () => {
    const toast = useToast();
    const [selectingPlan, setSelectingPlan] = useState<string | null>(null);

    const cardBg = brandConfig.colors.background.cardGradient;
    const borderColor = brandConfig.colors.border.darkCard;
    const textPrimary = brandConfig.colors.text.inverse;
    const textSecondary = brandConfig.colors.text.secondaryDark;
    const textMuted = brandConfig.colors.text.mutedDark;
    const currentTenantId = brandConfig.tenantId;

    const { data: tenantData, loading: tenantLoading, refetch } = useQuery(GET_CURRENT_TENANT, {
        variables: { id: currentTenantId },
    });

    const { data: productsData, loading: productsLoading } = useQuery(GET_SUBSCRIPTION_PRODUCTS, {
        variables: { tenantId: currentTenantId },
    });
    const subscriptionPlans: SubscriptionPlan[] = productsData?.publicSubscriptionProducts || [];

    const [updateTier] = useMutation(UPDATE_TENANT_TIER, {
        onCompleted: (data) => {
            const tier = data.updateTenant.subscriptionTier;
            toast({
                title: "Plan Updated!",
                description: `Your plan has been set to ${tier}. You'll be billed on the 1st of each month.`,
                status: "success",
                duration: 6000,
                isClosable: true,
            });
            setSelectingPlan(null);
            refetch();

            if (!tenantData?.tenant?.stripeCustomerId) {
                setTimeout(() => {
                    toast({
                        title: "Save a Payment Method",
                        description: "Add a card on file to make bill payments easier.",
                        status: "info",
                        duration: 8000,
                        isClosable: true,
                    });
                }, 2000);
            }
        },
        onError: (err) => {
            toast({
                title: "Failed to update plan",
                description: err.message,
                status: "error",
                duration: 5000,
            });
            setSelectingPlan(null);
        },
    });

    const currentTier = tenantData?.tenant?.subscriptionTier;

    const handleSelectPlan = (plan: SubscriptionPlan) => {
        if (plan.tier === currentTier) return;
        setSelectingPlan(plan.id);
        updateTier({
            variables: {
                id: currentTenantId,
                input: { subscriptionTier: plan.tier },
            },
        });
    };

    if (tenantLoading || productsLoading) {
        return (
            <VStack spacing={4}>
                <Spinner size="xl" />
                <Text color={textSecondary}>Loading plans...</Text>
            </VStack>
        );
    }

    return (
        <VStack spacing={8} align="stretch">
            <VStack spacing={3} textAlign="center">
                <HStack spacing={3} justify="center">
                    <Heading size="2xl" color={textPrimary}>
                        Progressive Business Growth Plans
                    </Heading>
                    <Badge colorScheme="purple" fontSize="xs" px={2} py={0.5} borderRadius="md">
                        CUSTOMER
                    </Badge>
                </HStack>
                <Text fontSize="lg" color={textSecondary}>
                    Start with Foundation and scale as you grow. Each tier builds on the previous.
                </Text>
                <Text fontSize="md" color={textMuted}>
                    All plans are month-to-month. Billed on the 1st — pay with card or reward tokens.
                </Text>
            </VStack>

            {currentTier && (
                <Alert status="info" bg={cardBg} borderColor={borderColor}>
                    <AlertIcon />
                    <Box>
                        <Text fontWeight="bold" color={textPrimary}>
                            Current plan: {currentTier}
                        </Text>
                        <Text fontSize="sm" color={textSecondary}>
                            Manage your payment methods in your{" "}
                            <Button
                                as="a"
                                href="/profile/payment-methods"
                                variant="link"
                                size="sm"
                                color={brandConfig.colors.primary}
                            >
                                payment settings
                            </Button>
                        </Text>
                    </Box>
                </Alert>
            )}

            {subscriptionPlans.length === 0 ? (
                <Alert status="warning" bg={cardBg} borderColor={borderColor}>
                    <AlertIcon />
                    <Text color={textPrimary}>No subscription plans available.</Text>
                </Alert>
            ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
                    {subscriptionPlans.map((plan) => (
                        <SubscriptionPlanCard
                            key={plan.id}
                            plan={plan}
                            isCurrentPlan={plan.tier === currentTier}
                            isSelecting={selectingPlan === plan.id}
                            onSelect={handleSelectPlan}
                        />
                    ))}
                </SimpleGrid>
            )}

            <AddonsSection />
        </VStack>
    );
};

export default SubscriptionOffers;
